/**
 * Document extraction outcome (CUR-1418).
 *
 * The pipeline persists two facts about a file: an `ExtractionStatus` and, on
 * failure, the raw text the failing stage threw (`File.extractionError`). Neither
 * is written for a person: the raw text carries provider HTTP bodies, JSON
 * validation dumps and ops instructions such as "Configure Mistral OCR", and a
 * `SKIPPED` status never says why. This module turns those two facts, plus the
 * file's MIME type, into one user-facing outcome with a kind, a sentence a claims
 * handler can act on, and whether a retry can change anything.
 *
 * Pure and shared so the Documents table (client, over `listFiles` rows) and the
 * processing drawer (server, `getExtractionResult`) describe a file the same way.
 * The server passes its own support check so a deployment without an OCR
 * provider reports a Mistral-only format as unsupported rather than skipped.
 */

import type { ExtractionStatus } from '../../types/document-processing';

/**
 * Non-standard MIME aliases normalised before any routing or support check.
 */
const MIME_TYPE_ALIASES: Record<string, string> = {
    'image/jpg': 'image/jpeg',
};

/**
 * Canonical form of a stored MIME type: lower-cased, parameters dropped
 * (`text/csv;charset=utf-8` → `text/csv`) and known aliases resolved
 * (`image/jpg` → `image/jpeg`). Every support check and extraction route reads
 * this form, so a browser-supplied charset can no longer fail the gate.
 */
export function normalizeMimeType(mimeType: string): string {
    const essence = mimeType.split(';', 1)[0]?.trim().toLowerCase() ?? '';
    return MIME_TYPE_ALIASES[essence] ?? essence;
}

/**
 * Formats every deployment can extract text from, with the label the UI names
 * them by. Order is the order the supported-formats sentence lists them in.
 * The server's processable allow-list is asserted equal to these keys.
 */
export const DOCUMENT_PROCESSING_SUPPORTED_FORMATS = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word (.docx)',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel (.xlsx)',
    'image/png': 'PNG',
    'image/jpeg': 'JPEG',
    'image/webp': 'WebP',
    'image/gif': 'GIF',
    'text/plain': 'plain text',
    'text/csv': 'CSV',
    'message/rfc822': 'email (.eml)',
    'application/vnd.ms-outlook': 'Outlook email (.msg)',
} as const satisfies Record<string, string>;

/**
 * Formats only an OCR provider can read. Supported when the deployment has
 * Mistral OCR configured; the server's gate decides that at request time.
 */
export const DOCUMENT_PROCESSING_OCR_ONLY_FORMATS = {
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint (.pptx)',
    'image/avif': 'AVIF',
} as const satisfies Record<string, string>;

export const DOCUMENT_PROCESSING_SUPPORTED_MIME_TYPES = Object.keys(
    DOCUMENT_PROCESSING_SUPPORTED_FORMATS,
) as readonly (keyof typeof DOCUMENT_PROCESSING_SUPPORTED_FORMATS)[];

export const DOCUMENT_PROCESSING_OCR_ONLY_MIME_TYPES = Object.keys(
    DOCUMENT_PROCESSING_OCR_ONLY_FORMATS,
) as readonly (keyof typeof DOCUMENT_PROCESSING_OCR_ONLY_FORMATS)[];

const FORMAT_LABELS: Record<string, string> = {
    ...DOCUMENT_PROCESSING_SUPPORTED_FORMATS,
    ...DOCUMENT_PROCESSING_OCR_ONLY_FORMATS,
    'image/jpg': 'JPEG',
    'image/tiff': 'TIFF',
    'image/bmp': 'BMP',
    'image/heic': 'HEIC',
    'image/heif': 'HEIF',
    'image/svg+xml': 'SVG',
    'application/msword': 'Word 97-2003 (.doc)',
    'application/vnd.ms-excel': 'Excel 97-2003 (.xls)',
    'application/vnd.ms-powerpoint': 'PowerPoint 97-2003 (.ppt)',
    'application/zip': 'ZIP archive',
    'application/octet-stream': 'unrecognised binary',
    'application/json': 'JSON',
    'text/html': 'HTML',
    'video/mp4': 'MP4 video',
    'video/quicktime': 'QuickTime video',
};

/** Human label for a MIME type, falling back to the type itself; empty types read as "unknown". */
export function describeMimeType(mimeType: string | null | undefined): string {
    if (!mimeType) {
        return 'unknown';
    }
    const normalized = normalizeMimeType(mimeType);
    return FORMAT_LABELS[normalized] ?? (normalized || 'unknown');
}

/** The sentence fragment the UI lists supported formats with, e.g. "PDF, Word (.docx), …". */
export function listSupportedFormats(options: { includeOcrOnlyFormats: boolean }): string {
    const labels = [
        ...Object.values(DOCUMENT_PROCESSING_SUPPORTED_FORMATS),
        ...(options.includeOcrOnlyFormats ? Object.values(DOCUMENT_PROCESSING_OCR_ONLY_FORMATS) : []),
    ];
    return labels.join(', ');
}

/**
 * Whether a MIME type has an extraction route in every deployment. The server
 * layers the OCR-only formats on top when Mistral OCR is configured.
 */
export function isAlwaysSupportedMimeType(mimeType: string | null | undefined): boolean {
    if (!mimeType) {
        return false;
    }
    return (DOCUMENT_PROCESSING_SUPPORTED_MIME_TYPES as readonly string[]).includes(normalizeMimeType(mimeType));
}

export const EXTRACTION_OUTCOME_KINDS = [
    /** Processing was cancelled; the user can start it again. */
    'cancelled',
    /** Text extraction succeeded but enrichment has persisted warnings. */
    'enrichment-warning',
    /** No extractor for this MIME type; a retry cannot help. */
    'unsupported-format',
    /** The format is supported but processing was not run (entity or category has it off). */
    'processing-disabled',
    /** The file exceeds a size, page or archive limit. */
    'file-too-large',
    /** The bytes could not be read as the declared format: corrupted, encrypted or mislabelled. */
    'corrupted-file',
    /** The extraction or AI provider failed transiently; reprocessing usually succeeds. */
    'provider-error',
    /** The stored file has no bytes at all; only uploading it again can help. */
    'empty-file',
    /** Extraction ran but produced no text worth keeping. */
    'empty-content',
    /** Failed under an earlier extractor that could not read a format this one can; a reprocess fixes it. */
    'outdated-failure',
    /** Anything the patterns above do not recognise. */
    'unknown-error',
] as const;

export type ExtractionOutcomeKind = (typeof EXTRACTION_OUTCOME_KINDS)[number];

export interface ExtractionOutcome {
    kind: ExtractionOutcomeKind;
    /** Short label for a badge, e.g. "Unsupported format". */
    label: string;
    /** One or two sentences the user can act on. */
    message: string;
    /** Whether reprocessing the file can plausibly change the result. */
    retryable: boolean;
    /** The raw persisted error, kept for a details toggle; null when there is none. */
    details: string | null;
}

/**
 * Persisted as `File.extractionError` when the stored object is zero bytes, so
 * extraction is settled before any extractor runs. Matched by the `empty-file`
 * pattern below; a reprocess cannot change the result.
 */
export const EMPTY_STORED_FILE_ERROR = 'The file is empty (0 bytes), so there is no content to extract.';

/** Ordered: the first matching pattern wins, so the specific kinds sit above the generic ones. */
const ERROR_KIND_PATTERNS: ReadonlyArray<readonly [ExtractionOutcomeKind, RegExp]> = [
    ['empty-file', /file is empty \(0 bytes\)/i],
    [
        'unsupported-format',
        /cannot extract from mime type|unsupported mime type|no suitable extraction method|mistral ocr is required|unsupported_type|cannot extract preliminary content/i,
    ],
    [
        'file-too-large',
        /exceeds the limit|exceeds maximum|too large|too many pages|file size|page limit|max(?:imum)? pages/i,
    ],
    [
        'corrupted-file',
        /corrupt|encrypted|password|not a valid|invalid (?:pdf|docx|xlsx|zip|archive|file|input)|could not (?:parse|read|open)|failed to parse (?:pdf|docx|xlsx|the file)|status 400|status 415|status 422|malformed/i,
    ],
    ['empty-content', /returned empty content|no text (?:content|found|extracted)|empty (?:document|text|extraction)/i],
    [
        'provider-error',
        /status 5\d\d|status 429|status 408|timeout|timed out|aborted|rate limit|provider error|ai provider|econnreset|econnrefused|socket hang up|fetch failed|location is not supported|failed to parse ai response|service unavailable|overloaded/i,
    ],
];

/**
 * Classify the raw persisted extraction error. Exported for the badge tooltip,
 * which only has the error text.
 */
export function classifyExtractionError(error: string): ExtractionOutcomeKind {
    for (const [kind, pattern] of ERROR_KIND_PATTERNS) {
        if (pattern.test(error)) {
            return kind;
        }
    }
    return 'unknown-error';
}

interface DescribeExtractionOutcomeInput {
    extractionStatus: ExtractionStatus | 'CANCELLED' | null | undefined;
    extractionError: string | null | undefined;
    mimeType: string | null | undefined;
    /**
     * Whether this deployment can extract the file's format. Defaults to the
     * always-supported list; the server passes its OCR-aware gate.
     */
    isSupportedMimeType?: (mimeType: string | null | undefined) => boolean;
    /** Whether the supported-formats sentence names the OCR-only formats. Defaults to false, matching the always-supported classification. */
    includeOcrOnlyFormats?: boolean;
}

/**
 * Describe a terminal extraction outcome for display. Returns null while the
 * file is pending, processing, completed without warnings or has never been queued: those states
 * are already legible from the status badge alone.
 */
export function describeExtractionOutcome(input: DescribeExtractionOutcomeInput): ExtractionOutcome | null {
    const {
        extractionStatus,
        extractionError,
        mimeType,
        isSupportedMimeType = isAlwaysSupportedMimeType,
        includeOcrOnlyFormats = false,
    } = input;
    const details = extractionError?.trim() ? extractionError.trim() : null;
    const format = describeMimeType(mimeType);
    const supported = listSupportedFormats({ includeOcrOnlyFormats });

    if (extractionStatus === 'SKIPPED') {
        if (!isSupportedMimeType(mimeType)) {
            return {
                kind: 'unsupported-format',
                label: 'Unsupported format',
                message: `${capitalise(format)} files cannot be processed. Supported formats: ${supported}.`,
                retryable: false,
                details,
            };
        }
        return {
            kind: 'processing-disabled',
            label: 'Skipped',
            message: 'Document processing was not run for this file.',
            retryable: true,
            details,
        };
    }

    if (extractionStatus === 'COMPLETED' && details) {
        return {
            kind: 'enrichment-warning',
            label: 'Processed with warnings',
            message:
                'Text extraction succeeded, but some additional processing, such as summary or metadata generation, did not complete. Reprocess the file to try again.',
            retryable: true,
            details,
        };
    }

    if (extractionStatus === 'CANCELLED') {
        return {
            kind: 'cancelled',
            label: 'Cancelled',
            message: 'Processing was cancelled before it finished. Reprocess the file to run it again.',
            retryable: true,
            details,
        };
    }

    if (extractionStatus !== 'FAILED') {
        return null;
    }

    const kind = details ? classifyExtractionError(details) : 'unknown-error';
    switch (kind) {
        case 'unsupported-format':
            // The error says the format had no extractor, but this deployment has one
            // now (WebP and GIF gained an LLM-vision route in March 2026): the row is
            // stale, not unsupported, and a reprocess is exactly what fixes it.
            if (isSupportedMimeType(mimeType)) {
                return {
                    kind: 'outdated-failure',
                    label: 'Failed',
                    message: `This file failed under an earlier version of document processing that could not read ${format} files. Reprocess it to try again.`,
                    retryable: true,
                    details,
                };
            }
            return {
                kind,
                label: 'Unsupported format',
                message: `${capitalise(format)} files cannot be processed. Supported formats: ${supported}.`,
                retryable: false,
                details,
            };
        case 'file-too-large':
            return {
                kind,
                label: 'Failed',
                message: 'The file is too large to process. Reduce its size or split it and upload again.',
                retryable: false,
                details,
            };
        case 'corrupted-file':
            return {
                kind,
                label: 'Failed',
                message: `The file could not be read. It may be corrupted, password protected or not a valid ${format} file.`,
                retryable: false,
                details,
            };
        case 'empty-file':
            return {
                kind,
                label: 'Failed',
                message: 'The file is empty. Upload it again.',
                retryable: false,
                details,
            };
        case 'empty-content':
            return {
                kind,
                label: 'Failed',
                message: 'No readable text was found in this file.',
                retryable: true,
                details,
            };
        case 'provider-error':
            return {
                kind,
                label: 'Failed',
                message:
                    'Processing failed because of a temporary problem with the extraction service. Reprocess the file to try again.',
                retryable: true,
                details,
            };
        default:
            return {
                kind: 'unknown-error',
                label: 'Failed',
                message: 'Processing failed. Reprocess the file; if it fails again, contact support.',
                retryable: true,
                details,
            };
    }
}

function capitalise(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
}
